import React from "react";

import {
  faSearch,
  faWarning,
  IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";

import { DisplayDateTime } from "src/base-components/Date";
import { PillVariants } from "src/base-components/Pill";
import {
  PropertiesList,
  PropertiesListItem,
} from "src/base-components/PropertiesList";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { TagPill } from "src/base-components/TagPill";
import { CaseBlock, CaseBlockProps } from "src/cases/caseView/CaseBlock";
import { CaseStatusPill } from "src/cases/common/CaseStatusPill";
import { useGetCasePropertyIcon } from "src/cases/common/useGetCasePropertyIcon";
import { useCase } from "src/cases/queries/cases";
import {
  CaseEnvironment,
  CasePropertiesValue,
} from "src/clients/case-management";
import { EmptyState } from "src/design-system/EmptyState";
import { EntityDetailsWindowPill } from "src/entities/entityView/EntityDetailsWindowPill";
import { Environment } from "src/hooks/useEnvironment";
import { assertUnreachable } from "src/utils/typeUtils";

type CaseDetailsBlockProps = Omit<CaseBlockProps, "children"> & {
  caseId: string | undefined | null;
  caseEnv: CaseEnvironment;
};

const renderEnum = (
  value: string,
  schema: Extract<CasePropertiesValue, { type: "enum" }>,
  caseProperties: Record<string, unknown>,
  propertyKey: string,
): React.ReactNode => {
  const enumValue = schema.values.find((v) => v.value === value);

  if (propertyKey === "status") {
    return (
      <CaseStatusPill
        status={value}
        subStatus={caseProperties.sub_status as string | undefined}
        variant={enumValue?.color as PillVariants}
      />
    );
  }

  return <TagPill size="sm" value={enumValue || value} />;
};

const renderByType = ({
  value,
  schema,
  caseProperties,
  key,
  icon,
  caseEnv,
}: {
  value: unknown;
  schema: CasePropertiesValue;
  caseProperties: Record<string, unknown>;
  key: string;
  icon?: IconDefinition;
  caseEnv?: CaseEnvironment;
}): React.ReactNode => {
  switch (schema.type) {
    case "string":
      return <span className="font-inter-normal-12px">{String(value)}</span>;

    case "datetime":
      return (
        <span className="font-inter-normal-12px">
          {typeof value === "string" ? (
            <DisplayDateTime value={value} />
          ) : (
            String(value)
          )}
        </span>
      );

    case "tags":
      if (Array.isArray(value)) {
        return (
          <div className="flex flex-wrap gap-1">
            {value.map((tag, index) => {
              const tagValue = schema.values?.find((v) => v.value === tag);
              return <TagPill key={index} value={tagValue || String(tag)} />;
            })}
          </div>
        );
      }
      return <span className="font-inter-normal-12px">{String(value)}</span>;

    case "enum": {
      return renderEnum(String(value), schema, caseProperties, key);
    }

    case "event_ref": {
      return <span className="font-inter-normal-12px">{String(value)}</span>;
    }

    case "entity_ref": {
      return (
        <EntityDetailsWindowPill
          entityIcon={icon}
          environment={caseEnv as Environment}
          id={value as string}
          name={value as string}
          schemaId={schema.entity_type}
          isPinnable
        />
      );
    }

    default:
      assertUnreachable(schema);
  }
};

export const CaseDetailsBlock: React.FC<CaseDetailsBlockProps> = ({
  caseId,
  caseEnv,
  ...props
}) => {
  const { data: caseData, isLoading, error: caseError } = useCase(caseId);
  const getPropertyIcon = useGetCasePropertyIcon();

  const template = caseData?.data.template;
  const templateVersion = caseData?.data.template_version;

  if (!caseId) {
    return (
      <CaseBlock {...props}>
        <EmptyState
          dataLoc="case-details-no-case-id"
          description="This block currently has no linked case."
          headline="No case provided"
          icon={faSearch}
          size="sm"
        />
      </CaseBlock>
    );
  }

  if (isLoading) {
    return (
      <CaseBlock {...props}>
        <div className="flex h-full flex-col">
          <div className="mb-3" data-loc="case-details-description-loading">
            <SkeletonPlaceholder height="h-3.5" width="w-full" />
          </div>
          <PropertiesList isLoading={true} items={[]} loadingRows={5} />
        </div>
      </CaseBlock>
    );
  }

  if (!caseData || !templateVersion || !template || caseError) {
    return (
      <CaseBlock {...props}>
        <EmptyState
          dataLoc="case-details-no-case-data"
          description="Unable to load case data"
          headline="Error loading case"
          icon={faWarning}
          size="sm"
          variant="error"
        />
      </CaseBlock>
    );
  }

  const caseProperties = caseData.data.case_properties || {};
  const schemaProperties = templateVersion.case_properties;

  const propertyItems: PropertiesListItem[] = Object.entries(caseProperties)
    .filter(([key]) => schemaProperties[key]) // Only show properties that exist in schema
    .map(([key, value]) => {
      const schema = schemaProperties[key];
      const icon = getPropertyIcon(schema);

      return {
        key,
        label: schema.display_name || key,
        icon,
        value: renderByType({
          value,
          schema,
          caseProperties,
          key,
          icon,
          caseEnv,
        }),
      };
    });

  return (
    <CaseBlock {...props}>
      <div className="flex h-full flex-col">
        <PropertiesList dataLocPrefix="case-details" items={propertyItems} />
      </div>
    </CaseBlock>
  );
};
